"use client";

import { useState, type ReactNode } from "react";
import { Check, Copy, MessageCircle, Minus, Phone, Plus, ShieldCheck, Truck, RefreshCw, CreditCard } from "lucide-react";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";
import { isParagraphHtml, sanitizeRichHtml } from "../../utils/paragraphHtml";
import { emitAddToCart } from "../../runtime/cartBus";
import { StockBadge } from "../../components/StockBadge";

/**
 * Product "buy box" — the shop-style detail block for a `product` record. The dynamic-path deploy
 * authors this once in the product template with `{token}` placeholders for every SCALAR field
 * (price, consigne, reference, …); the engine substitutes them per record.
 *
 * Anatomy (modeled on the leading FR/BE turbo shops — autonorma, auto-platinium, mister-turbo,
 * alfi-turbo): breadcrumb → image + buy panel (title, ref/brand, badges, TTC price + HT note,
 * consigne, qty + add-to-cart, delivery promise, trust bar, phone/WhatsApp order row, package
 * contents) → always-visible product information sections. Details are intentionally
 * never hidden behind tabs so mobile and desktop shoppers can scan everything naturally.
 *
 * Compatibility rows come from the scalar `compatText` token: rows split on ";", cells on "|"
 * (Véhicule | Motorisation | Années); a row without "|" renders as one full-width cell.
 */
type Content = Record<string, unknown>;

/** Empty, or a still-unsubstituted `{token}` placeholder. */
function unresolved(v: unknown): boolean {
  const s = (v == null ? "" : String(v)).trim();
  return !s || /^\{.*\}$/.test(s);
}
function text(v: unknown): string {
  return unresolved(v) ? "" : String(v).trim();
}
function truthy(v: unknown): boolean {
  const s = text(v).toLowerCase();
  return s === "true" || s === "1" || s === "oui" || s === "yes";
}
/** Parse "339" / "339,50" / "1 299" to a number in euros; 0 when blank/unresolved. */
function euros(v: unknown): number {
  const s = text(v).replace(/\s/g, "").replace(",", ".");
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}
function money(n: number): string {
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " €";
}

const CONDITION: Record<string, string> = {
  reman: "Reconditionné (échange standard)",
  new: "Neuf",
  repair: "Réparation de votre pièce",
};
const STOCK: Record<string, { label: string; cls: string; ok: boolean }> = {
  "in-stock": { label: "En stock", cls: "bg-[#E6F4EA] text-[#1B7A3D]", ok: true },
  "on-order": { label: "Sur commande", cls: "bg-[#FFF4E0] text-[#9A6700]", ok: true },
  "out-of-stock": { label: "Rupture de stock", cls: "bg-[#FBE9E9] text-[#B42318]", ok: false },
};
const SECTION_LABEL: Record<string, string> = {
  turbos: "Turbos",
  injecteurs: "Injecteurs",
  "fap-dpf": "FAP / DPF",
  "pieces-detachees": "Pièces détachées",
};
const FAMILY_LABEL: Record<string, string> = {
  turbo: "Turbo",
  injecteur: "Injecteur",
  "fap-dpf": "FAP / DPF",
  chra: "CHRA",
  geometrie: "Géométrie",
  "kit-joints": "Kit joints",
  "piece-detachee": "Pièce détachée",
};
const PHONE_DISPLAY = "+32 495 77 97 78";
const PHONE_TEL = "tel:+32495779778";
const PHONE_WA = "https://wa.me/32495779778";
/** BE VAT — HT note next to the TTC price (competitor convention). */
const VAT = 1.21;

/** Reference chip with copy-to-clipboard (OEM / cross numbers). */
function RefChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable (http, permissions) — chip stays passive */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title="Copier la référence"
      className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-surface-1)] px-2.5 py-1 font-mono text-xs font-semibold text-[#121212] ring-1 ring-black/10 transition hover:ring-[#00B862]/50"
    >
      {value}
      {copied ? <Check className="h-3.5 w-3.5 text-[#00B862]" /> : <Copy className="h-3.5 w-3.5 text-[#9CA3AF]" />}
    </button>
  );
}

export function ProductBuyBoxRenderer({ component }: { component: { content?: Content } }) {
  const c = component.content ?? {};
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [coreOption, setCoreOption] = useState<"deposit" | "return_first">("deposit");
  const [activeImage, setActiveImage] = useState(0);

  const slug = text(c.slug);
  const title = text(c.title) || "Produit";
  const reference = text(c.reference);
  const brand = text(c.brand);
  const price = euros(c.price);
  const priceOld = euros(c.priceOld);
  const consigne = euros(c.consigne);
  const isExchange = truthy(c.isExchange);
  const allowReturnFirst = truthy(c.allowReturnFirst) && consigne > 0;
  const warranty = text(c.warrantyMonths);
  const stockStatus = text(c.stockStatus);
  const stock = STOCK[stockStatus] ?? STOCK["in-stock"];
  const isOrder = stockStatus === "on-order";
  const condition = CONDITION[text(c.condition)] ?? text(c.condition);
  const shortDescription = text(c.shortDescription);
  const body = text(c.body);
  const section = text(c.section);
  const family = text(c.family);
  const compatText = text(c.compatText);
  const gallery = [
    { src: c.image, alt: title },
    ...(Array.isArray(c.gallery)
      ? c.gallery.map((item) => {
          const value = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
          return { src: value.src, alt: text(value.alt) || title };
        })
      : []),
  ]
    .map((item) => ({ ...item, resolved: resolveImageValue(item.src as never).src }))
    .filter((item, index, items) => item.resolved && items.findIndex((candidate) => candidate.resolved === item.resolved) === index);
  const imageSrc = gallery[activeImage]?.resolved ?? gallery[0]?.resolved ?? "";
  const discount = priceOld > price && price > 0 ? Math.round(((priceOld - price) / priceOld) * 100) : 0;

  const add = () => {
    if (!stock.ok || !slug) return;
    emitAddToCart({
      slug,
      title,
      price,
      consigne,
      image: text(c.image),
      reference,
      quantity: qty,
      coreOption: consigne > 0 ? coreOption : "deposit",
    });
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2200);
  };

  const specs: Array<[string, string]> = [
    ["Référence", reference],
    ["Marque", brand],
    family ? ["Famille", FAMILY_LABEL[family] ?? family] : ["", ""],
    ["État", condition],
    warranty ? ["Garantie", `${warranty} mois`] : ["", ""],
    ["Disponibilité", stock.label],
  ].filter(([, v]) => v) as Array<[string, string]>;

  const oemList = text(c.oemNumbers).split(",").map((s) => s.trim()).filter(Boolean);
  const crossList = text(c.crossNumbers).split(",").map((s) => s.trim()).filter(Boolean);

  // Compatibility rows: "Make Model|Engine|Years; …" → cells. A row without "|"
  // renders as one wide cell (fallback for unstructured strings).
  const structuredCompat = Array.isArray(c.compatibility)
    ? c.compatibility
        .map((item) => {
          const fit = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
          const vehicle = [text(fit.make), text(fit.model)].filter(Boolean).join(" ");
          const engine = [text(fit.engine), text(fit.power), text(fit.engineCode) && `(${text(fit.engineCode)})`]
            .filter(Boolean)
            .join(" ");
          const years = [text(fit.yearFrom), text(fit.yearTo)].filter(Boolean).join("–");
          return [vehicle, engine, years].filter(Boolean);
        })
        .filter((row) => row.length > 0)
    : [];
  const compatRows = structuredCompat.length > 0
    ? structuredCompat
    : compatText
      ? compatText.split(";").map((r) => r.split("|").map((cell) => cell.trim()).filter(Boolean)).filter((r) => r.length > 0)
      : [];

  const sectionLabel = SECTION_LABEL[section];

  return (
    <article className="text-[#121212]">
      {/* Breadcrumb */}
      <nav aria-label="Fil d'Ariane" className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-[#6B7280]">
        <RuntimeLink href={{ slug: "/" }} className="transition hover:text-[#00B862]">Accueil</RuntimeLink>
        {sectionLabel ? (
          <>
            <span aria-hidden="true">›</span>
            <RuntimeLink href={{ slug: section }} className="transition hover:text-[#00B862]">{sectionLabel}</RuntimeLink>
          </>
        ) : null}
        <span aria-hidden="true">›</span>
        <span className="line-clamp-1 font-medium text-[#121212]">{title}</span>
      </nav>

      <div className="grid gap-8 md:grid-cols-2 md:gap-10">
        {/* Image */}
        <div className="self-start md:col-start-1">
          <div className="relative overflow-hidden rounded-2xl bg-[var(--color-surface-2)] ring-1 ring-black/10">
            {imageSrc ? (
              <RuntimeImage
                src={imageSrc}
                alt={gallery[activeImage]?.alt || title}
                className="aspect-[4/3] h-full w-full object-cover transition-transform duration-500 ease-out hover:scale-[1.04]"
                loading="eager"
              />
            ) : (
              <div className="aspect-[4/3] w-full" />
            )}
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(80% 80% at 50% 45%, transparent 55%, rgba(10,10,10,.05))" }}
              aria-hidden="true"
            />
          </div>
          {gallery.length > 1 ? (
            <div className="mt-3 grid grid-cols-4 gap-2" aria-label="Galerie produit">
              {gallery.slice(0, 4).map((item, index) => (
                <button
                  key={item.resolved}
                  type="button"
                  onClick={() => setActiveImage(index)}
                  className={`overflow-hidden rounded-lg bg-[var(--color-surface-2)] ring-2 transition ${activeImage === index ? "ring-[#00B862]" : "ring-transparent hover:ring-[#00B862]/40"}`}
                  aria-label={`Afficher l'image ${index + 1}`}
                >
                  <RuntimeImage src={item.resolved} alt="" className="aspect-[4/3] h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Buy panel — right column spans the image + tabs rows; the inner
            wrapper is the sticky element. Tracks the measured header height. */}
        <div className="min-w-0 md:col-start-2 md:row-start-1 md:row-span-2">
        <div className="lg:sticky" style={{ top: "calc(var(--apa-header-h, 143px) + 16px)" }}>
          <h1 className="text-2xl font-extrabold leading-tight text-[#00B862] md:text-3xl" style={{ fontFamily: "var(--font-aleo)" }}>
            {title}
          </h1>
          {(reference || brand) && (
            <p className="mt-2 text-sm text-[#6B7280] md:text-base">
              {reference && <>Réf. <span className="font-semibold text-[#121212]">{reference}</span></>}
              {reference && brand && " · "}
              {brand && <span className="font-semibold text-[#121212]">{brand}</span>}
            </p>
          )}

          {/* Badges */}
          <div className="mt-4 flex flex-wrap gap-2">
            {isExchange && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-forest-100)] px-3 py-1 text-sm font-semibold text-[var(--color-forest-600)]">
                <RefreshCw className="h-4 w-4" /> Échange standard
              </span>
            )}
            <StockBadge quantity={text(c.stockQuantity)} stockStatus={stockStatus} size="md" />
            {warranty && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF4E0] px-3 py-1 text-sm font-semibold text-[#9A6700]">
                <ShieldCheck className="h-4 w-4" /> Garantie {warranty} mois
              </span>
            )}
          </div>

          {/* Price */}
          <div className="mt-5 flex flex-wrap items-end gap-3">
            <span className="inline-block border-b-[3px] border-[#00B862] pb-0.5 text-3xl font-extrabold text-[#121212] md:text-4xl">
              {money(price)}
            </span>
            <span className="pb-1 text-sm text-[#6B7280]">TTC</span>
            {priceOld > price && <span className="pb-1 text-lg text-[#9CA3AF] line-through">{money(priceOld)}</span>}
            {discount > 0 && (
              <span className="mb-1 rounded-md bg-[#FBE9E9] px-2 py-0.5 text-sm font-bold text-[#B42318]">-{discount}%</span>
            )}
          </div>
          {price > 0 && (
            <p className="mt-1 text-xs text-[#6B7280]">
              soit {(price / VAT).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € HT
            </p>
          )}
          {consigne > 0 && (
            <p className="mt-1.5 text-sm text-[#6B7280]">
              + consigne <span className="font-semibold text-[#121212]">{money(consigne)}</span> — remboursée au retour de votre ancienne pièce.
            </p>
          )}

          {consigne > 0 ? (
            <fieldset className="mt-5">
              <legend className="text-sm font-bold text-[#121212]">Choisissez votre mode d'échange</legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <label className={`cursor-pointer rounded-xl border p-3 transition ${coreOption === "deposit" ? "border-[#00B862] bg-[#E6F4EA]" : "border-black/10 bg-white hover:border-[#00B862]/40"}`}>
                  <input className="sr-only" type="radio" name="core-option" value="deposit" checked={coreOption === "deposit"} onChange={() => setCoreOption("deposit")} />
                  <span className="block text-sm font-bold text-[#121212]">Expédition immédiate</span>
                  <span className="mt-1 block text-xs leading-relaxed text-[#6B7280]">Je paie {money(consigne)} de consigne, remboursée après retour de l'ancienne pièce.</span>
                </label>
                {allowReturnFirst ? (
                  <label className={`cursor-pointer rounded-xl border p-3 transition ${coreOption === "return_first" ? "border-[#00B862] bg-[#E6F4EA]" : "border-black/10 bg-white hover:border-[#00B862]/40"}`}>
                    <input className="sr-only" type="radio" name="core-option" value="return_first" checked={coreOption === "return_first"} onChange={() => setCoreOption("return_first")} />
                    <span className="block text-sm font-bold text-[#121212]">Ancienne pièce d'abord</span>
                    <span className="mt-1 block text-xs leading-relaxed text-[#6B7280]">Aucune consigne : l'expédition démarre après réception et acceptation de votre pièce.</span>
                  </label>
                ) : null}
              </div>
            </fieldset>
          ) : null}

          {shortDescription && <p className="mt-4 text-base leading-relaxed text-[#121212]/90">{shortDescription}</p>}

          {/* Quantity + add to cart */}
          <div className="mt-6 flex flex-wrap items-stretch gap-3">
            <div className="inline-flex items-center rounded-xl ring-1 ring-[#00B862]/20">
              <button type="button" aria-label="Diminuer" onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="grid h-12 w-12 place-items-center text-[#00B862] hover:bg-[var(--color-surface-1)]">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center text-lg font-semibold tabular-nums">{qty}</span>
              <button type="button" aria-label="Augmenter" onClick={() => setQty((q) => Math.min(20, q + 1))}
                className="grid h-12 w-12 place-items-center text-[#00B862] hover:bg-[var(--color-surface-1)]">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button type="button" onClick={add} disabled={!stock.ok}
              className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[#00B862] px-6 text-base font-semibold text-white transition hover:bg-[#00935A] hover:shadow-[var(--shadow-glow)] active:translate-y-px disabled:cursor-not-allowed disabled:bg-[#9CA3AF] disabled:shadow-none">
              {added ? (<><Check className="h-5 w-5" /> Ajouté au panier</>) : (<><CreditCard className="h-5 w-5" /> {stock.ok ? "Ajouter au panier" : "Indisponible"}</>)}
            </button>
          </div>

          {/* Delivery promise (competitor pattern: concrete, above the fold) */}
          {stock.ok ? (
            <p className="mt-3 flex items-center gap-2 text-sm font-medium text-[var(--color-forest-600)]">
              <Truck className="h-4 w-4 shrink-0" />
              {isOrder
                ? "Sur commande — délai communiqué sous 24h."
                : "En stock — expédié sous 24h ouvrées, livraison 2-3 jours (BE/FR)."}
            </p>
          ) : null}

          {/* Trust bar */}
          <div className="mt-6 grid grid-cols-1 gap-3 rounded-xl bg-[var(--color-surface-1)] p-4 ring-1 ring-black/5 sm:grid-cols-3">
            <TrustItem icon={<Truck className="h-5 w-5" />} label="Expédition 24/48h" />
            <TrustItem icon={<ShieldCheck className="h-5 w-5" />} label="Pièces garanties" />
            <TrustItem icon={<RefreshCw className="h-5 w-5" />} label="Consigne remboursée" />
          </div>

          {/* Phone / WhatsApp order row */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <a href={PHONE_TEL} className="inline-flex items-center gap-2 font-semibold text-[#121212] transition hover:text-[#00B862]">
              <Phone className="h-4 w-4 text-[#00B862]" /> {PHONE_DISPLAY}
              <span className="font-normal text-[#6B7280]">(lun–ven 9h–18h · sam–dim 9h–14h)</span>
            </a>
            <a
              href={PHONE_WA}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#00B862]/40 px-3 py-1 font-semibold text-[var(--color-forest-600)] transition hover:bg-[var(--color-forest-100)]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>

          {/* Package contents (échange standard) */}
          {isExchange ? (
            <div className="mt-5 rounded-xl border border-black/10 p-4">
              <p className="text-sm font-bold text-[#121212]">Contenu du colis</p>
              <ul className="mt-2 grid gap-1.5 text-sm text-[#121212]/85 sm:grid-cols-2">
                {["Seringue d'amorçage", "Visserie complète", "Rapport de test banc", "Étiquette retour prépayée"].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="h-4 w-4 shrink-0 text-[#00B862]" /> {item}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-[#6B7280]">Kit de joints disponible en option.</p>
            </div>
          ) : null}
        </div>
        </div>

        {/* All product information stays visible. There are no horizontal tab or
            table affordances, so narrow screens get the same content as desktop. */}
        <section aria-label="Informations produit" className="min-w-0 space-y-5 md:col-span-2">
          {body ? (
            <section className="overflow-hidden rounded-2xl border border-black/10 bg-white">
              <ProductInfoHeading kicker="À propos" title="Description" subtitle="L'essentiel sur cette pièce et son reconditionnement." />
              <div className="p-5 md:p-6">
                {isParagraphHtml(body) ? (
                  <div className="rich-paragraph-block max-w-3xl text-[15px] leading-relaxed text-[#121212]" dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(body) }} />
                ) : (
                  <p className="max-w-3xl text-[15px] leading-relaxed text-[#121212]">{body}</p>
                )}
              </div>
            </section>
          ) : null}

          <section className="overflow-hidden rounded-2xl border border-black/10 bg-white">
            <ProductInfoHeading kicker="Données produit" title="Fiche technique" subtitle="Références, état, marque et disponibilité en un coup d'œil." />
            <div className="p-5 md:p-6">
              <dl className="grid overflow-hidden rounded-xl border border-black/8 sm:grid-cols-2">
                {specs.map(([k, v]) => (
                  <div key={k} className="flex min-w-0 items-start justify-between gap-4 border-b border-black/5 bg-[#F8FAF9] px-4 py-3.5 sm:[&:nth-last-child(-n+2)]:border-b-0">
                    <dt className="text-sm font-medium text-[#6B7280]">{k}</dt>
                    <dd className="min-w-0 break-words text-right text-sm font-bold text-[#121212]">{v}</dd>
                  </div>
                ))}
              </dl>
              {oemList.length > 0 ? (
                <div className="mt-5">
                  <p className="text-xs font-extrabold uppercase tracking-[.12em] text-[#5F6863]">Numéros OEM / origine</p>
                  <div className="mt-2.5 flex flex-wrap gap-2">{oemList.map((n) => <RefChip key={n} value={n} />)}</div>
                </div>
              ) : null}
              {crossList.length > 0 ? (
                <div className="mt-5 border-t border-black/5 pt-5">
                  <p className="text-xs font-extrabold uppercase tracking-[.12em] text-[#5F6863]">Références croisées / équivalences</p>
                  <div className="mt-2.5 flex flex-wrap gap-2">{crossList.map((n) => <RefChip key={n} value={n} />)}</div>
                </div>
              ) : null}
            </div>
          </section>

          {compatRows.length > 0 ? (
            <section className="overflow-hidden rounded-2xl border border-black/10 bg-white">
              <ProductInfoHeading kicker={`${compatRows.length} application${compatRows.length > 1 ? "s" : ""} publiée${compatRows.length > 1 ? "s" : ""}`} title="Compatibilités véhicule" subtitle="Contrôlez le véhicule, la motorisation et les années sans ouvrir un autre panneau." />
              <div className="grid gap-3 p-5 sm:grid-cols-2 md:p-6 lg:grid-cols-3">
                {compatRows.map((row, index) => (
                  <article key={`${row.join("-")}-${index}`} className="min-w-0 rounded-xl border border-black/8 bg-[#F7F9F8] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] font-extrabold uppercase tracking-[.14em] text-[#00935A]">Véhicule</p>
                        <h3 className="mt-1 break-words text-sm font-extrabold leading-snug text-[#121212]">{row[0]}</h3>
                      </div>
                      <span className="grid h-7 min-w-7 shrink-0 place-items-center rounded-lg bg-[#E2F7ED] px-2 text-[10px] font-extrabold text-[#00884E]">{String(index + 1).padStart(2, "0")}</span>
                    </div>
                    {row.length >= 2 ? (
                      <dl className="mt-3 grid gap-2 border-t border-black/5 pt-3 text-xs">
                        <div className="grid grid-cols-[88px_1fr] gap-2"><dt className="text-[#6B7280]">Motorisation</dt><dd className="min-w-0 break-words font-semibold text-[#121212]">{row[1]}</dd></div>
                        <div className="grid grid-cols-[88px_1fr] gap-2"><dt className="text-[#6B7280]">Années</dt><dd className="font-semibold text-[#121212]">{row[2] ?? "—"}</dd></div>
                      </dl>
                    ) : null}
                  </article>
                ))}
              </div>
              <div className="mx-5 mb-5 flex items-start gap-2.5 rounded-xl bg-[#EAF8F1] p-3.5 text-xs leading-relaxed text-[#315344] md:mx-6 md:mb-6">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#00A45A]" /> Un doute ? Envoyez votre VIN ou votre référence OEM : notre atelier confirme la compatibilité avant expédition.
              </div>
            </section>
          ) : null}

          <section className="overflow-hidden rounded-2xl border border-black/10 bg-white">
            <ProductInfoHeading kicker="Service APA Parts" title="Livraison & consigne" subtitle="Des délais et une procédure de retour clairement annoncés." />
            <div className="space-y-4 p-5 text-sm leading-relaxed text-[#303632] md:p-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-[#F7F9F8] p-4"><Truck className="h-5 w-5 text-[#00A45A]" /><p className="mt-3 font-extrabold text-[#121212]">Expédition sous 24h ouvrées</p><p className="mt-1 text-xs leading-relaxed text-[#6B7280]">Commande avant 15h, puis livraison suivie en 2 à 3 jours ouvrés en Belgique et en France.</p></div>
                <div className="rounded-xl bg-[#F7F9F8] p-4"><CreditCard className="h-5 w-5 text-[#00A45A]" /><p className="mt-3 font-extrabold text-[#121212]">Livraison offerte dès 200 €</p><p className="mt-1 text-xs leading-relaxed text-[#6B7280]">Le suivi est transmis par e-mail dès la prise en charge de votre colis.</p></div>
              </div>
              {consigne > 0 ? (
                <div className="rounded-xl border border-[#00B862]/20 bg-[#EDFAF4] p-4">
                  <p className="font-extrabold text-[#007E48]">Consigne de {money(consigne)}, intégralement remboursée</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-[#315344]">Une étiquette prépayée est fournie. Replacez l&apos;ancienne pièce dans l&apos;emballage d&apos;origine : le remboursement est effectué sous 7 jours après réception.</p>
                </div>
              ) : null}
              <p>Une question sur la compatibilité ou le délai ? Appelez-nous au <a href={PHONE_TEL} className="font-bold text-[#008B51] underline underline-offset-2">{PHONE_DISPLAY}</a> — nous vérifions la référence avec vous avant l&apos;envoi.</p>
            </div>
          </section>
        </section>
      </div>
    </article>
  );
}

function ProductInfoHeading({ kicker, title, subtitle }: { kicker: string; title: string; subtitle: string }) {
  return (
    <header className="flex items-start gap-3.5 border-b border-black/5 bg-[#F8FAF9] px-5 py-4 md:px-6 md:py-5">
      <span className="mt-0.5 h-10 w-1 shrink-0 rounded-full bg-[#00B862]" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-[10px] font-extrabold uppercase tracking-[.16em] text-[#00935A]">{kicker}</p>
        <h2 className="mt-0.5 text-lg font-bold leading-tight text-[#121212]" style={{ fontFamily: "var(--font-aleo)" }}>{title}</h2>
        <p className="mt-1 text-xs leading-relaxed text-[#6B7280]">{subtitle}</p>
      </div>
    </header>
  );
}

function TrustItem({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2.5 text-sm font-semibold text-[#121212]">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-[#00B862] ring-1 ring-[#00B862]/15">{icon}</span>
      {label}
    </div>
  );
}
